import { useState, useEffect } from 'react';
import { Icons } from '../components/Icons';
import { MetricsCard } from '../components/portfolio/MetricsCard';
import { TooltipTerm } from '../components/ui/Tooltip';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { encryptionService } from '../services/encryptionService';
import { cryptoComAPI, TradingBot, StakingPosition, CROCard } from '../lib/api/cryptocom-api';
import { useTradingBots, useStakingPositions, useCROCard, useCryptoComStats } from '../hooks/useCryptoCom';
import { BotPerformanceChart } from '../components/cryptocom/BotPerformanceChart';
import { StakingRewardsChart } from '../components/cryptocom/StakingRewardsChart';
import { BotFilters } from '../components/cryptocom/BotFilters';
import { ExportData } from '../components/cryptocom/ExportData';
import { CardBenefitsCalculator } from '../components/cryptocom/CardBenefitsCalculator';
import { APYComparisonChart } from '../components/cryptocom/APYComparisonChart';
import { TierProgressionVisualization } from '../components/cryptocom/TierProgressionVisualization';
import { SmartRecommendations } from '../components/cryptocom/SmartRecommendations';

/**
 * Crypto.com Dashboard - Trading & Staking Bot Manager
 *
 * IMPROVED VERSION with React Query:
 * - Auto-refresh every 30-60s
 * - Intelligent caching
 * - Automatic retry on errors
 * - Loading states & error handling
 * - Tooltips on technical terms
 * - Empty states with CTAs
 * - Credentials persistence
 * - Action buttons
 * - Help links
 */

export function CryptoComDashboard() {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [useDemoMode, setUseDemoMode] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'bots' | 'staking' | 'card'>('bots');
  const [filteredBots, setFilteredBots] = useState<TradingBot[]>([]);

  // Load saved credentials on mount and set them in API
  useEffect(() => {
    const loadCredentials = async () => {
      const savedCreds = await encryptionService.getCredentials<{
        apiKey: string;
        apiSecret: string;
        demoMode: boolean;
      }>('cryptocom_credentials');

      if (savedCreds) {
        setApiKey(savedCreds.apiKey);
        setApiSecret(savedCreds.apiSecret);
        setUseDemoMode(savedCreds.demoMode ?? true);
        cryptoComAPI.setCredentials(savedCreds);
        cryptoComAPI.setDemoMode(savedCreds.demoMode ?? true);
        setIsConnected(true);
      }
    };

    loadCredentials();
  }, []);

  // React Query hooks - only fetch when connected
  const {
    data: tradingBots = [],
    isLoading: botsLoading,
    error: botsError
  } = useTradingBots({
    enabled: isConnected
  });

  const {
    data: stakingPositions = [],
    isLoading: stakingLoading,
    error: stakingError
  } = useStakingPositions({
    enabled: isConnected
  });

  const {
    data: croCard = null,
    isLoading: cardLoading,
    error: cardError
  } = useCROCard({
    enabled: isConnected
  });

  // Get combined stats
  const {
    totalTradingProfit,
    totalStakingRewards,
    totalStakedValue,
    activeBots
  } = useCryptoComStats();

  // Initialize filtered bots when tradingBots changes
  useEffect(() => {
    setFilteredBots(tradingBots);
  }, [tradingBots]);

  const getBotTypeColor = (type: TradingBot['type']) => {
    const colors = {
      GRID: '#8b5cf6',
      DCA: '#10b981',
      TWAP: '#3b82f6',
      FUNDING_ARBITRAGE: '#f59e0b'
    };
    return colors[type];
  };

  const getBotTypeTooltip = (type: TradingBot['type']) => {
    const tooltips = {
      GRID: 'Grid Trading : Place des ordres d\'achat et de vente à intervalles réguliers pour profiter de la volatilité',
      DCA: 'Dollar Cost Averaging : Achète automatiquement à intervalles réguliers pour lisser le prix d\'entrée',
      TWAP: 'Time-Weighted Average Price : Divise un gros ordre en petites tranches sur une période pour réduire l\'impact sur le prix',
      FUNDING_ARBITRAGE: 'Funding Arbitrage : Profite des écarts de taux de financement entre marchés spot et perpétuels'
    };
    return tooltips[type];
  };

  const getStakingTypeLabel = (type: StakingPosition['type']) => {
    const labels = {
      CRO_LOCKUP: 'CRO Lockup',
      CRO_SOFT_LOCKUP: 'Soft Lockup',
      DEFI_STAKING: 'DeFi Staking',
      EARN: 'Crypto Earn',
      CARD_STAKE: 'Card Stake'
    };
    return labels[type];
  };

  const getCardTierInfo = (tier: CROCard['tier']) => {
    const tiers = {
      MIDNIGHT_BLUE: { color: '#1a1a2e', cashback: 1, name: 'Midnight Blue', stake: 0 },
      RUBY_STEEL: { color: '#dc2626', cashback: 2, name: 'Ruby Steel', stake: 4000 },
      JADE_GREEN: { color: '#10b981', cashback: 3, name: 'Jade Green', stake: 40000 },
      ROYAL_INDIGO: { color: '#6366f1', cashback: 3, name: 'Royal Indigo', stake: 40000 },
      FROSTED_ROSE_GOLD: { color: '#f59e0b', cashback: 5, name: 'Frosted Rose Gold', stake: 400000 },
      ICY_WHITE: { color: '#f3f4f6', cashback: 5, name: 'Icy White', stake: 400000 },
      OBSIDIAN: { color: '#000000', cashback: 8, name: 'Obsidian', stake: 4000000 }
    };
    return tiers[tier];
  };

  const handleConnect = async () => {
    if (!apiKey || !apiSecret) {
      setConnectionError('Veuillez remplir tous les champs');
      return;
    }

    setIsConnecting(true);
    setConnectionError(null);

    try {
      const credentials = { apiKey, apiSecret };

      // Validate credentials with API (pass useDemoMode flag)
      const isValid = await cryptoComAPI.validateCredentials(credentials, !useDemoMode);

      if (!isValid) {
        throw new Error(useDemoMode ? 'Format de clés invalide' : 'Clés API invalides ou problème de connexion');
      }

      // Set credentials in API client
      cryptoComAPI.setCredentials(credentials);

      // Set demo mode
      cryptoComAPI.setDemoMode(useDemoMode);

      // 🔐 SECURITY: Encrypt and save credentials in IndexedDB
      await encryptionService.setCredentials('cryptocom_credentials', { ...credentials, demoMode: useDemoMode });

      // Mark as connected - this will enable React Query hooks
      setIsConnected(true);
    } catch (err) {
      setConnectionError(
        useDemoMode
          ? 'Les clés doivent contenir au moins 10 caractères.'
          : 'Échec de la connexion. Vérifiez vos clés API et votre connexion internet.'
      );
      cryptoComAPI.clearCredentials();
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    // Clear API credentials
    cryptoComAPI.clearCredentials();

    // Clear local state
    setIsConnected(false);
    setApiKey('');
    setApiSecret('');

    // Remove encrypted credentials from IndexedDB
    await encryptionService.removeCredentials('cryptocom_credentials');
  };

  const handleOpenCryptoComDocs = () => {
    window.open('https://exchange-docs.crypto.com/exchange/v1/rest-ws/index.html#introduction', '_blank');
  };

  if (!isConnected) {
    return (
      <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icons.CryptoCom size={28} />
          Connect to Crypto.com
        </h2>

        <div style={{
          padding: '16px',
          background: 'rgba(59, 130, 246, 0.1)',
          borderRadius: '8px',
          marginBottom: '24px',
          borderLeft: '3px solid #3b82f6'
        }}>
          <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '8px' }}>
            <strong>🔐 Connexion API Sécurisée</strong>
          </p>
          <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.6' }}>
            Connectez votre compte Crypto.com via API pour accéder à vos bots de trading, positions de staking et avantages carte.
            Vos clés sont stockées localement et ne sont jamais envoyées à nos serveurs.
          </p>
        </div>

        {connectionError && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '8px',
            borderLeft: '3px solid #ef4444',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Icons.AlertCircle size={18} style={{ color: '#ef4444' }} />
            <span style={{ fontSize: '13px', color: '#ef4444' }}>{connectionError}</span>
          </div>
        )}

        {/* Demo/Live Mode Switch */}
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          background: useDemoMode ? 'rgba(245, 158, 11, 0.05)' : 'rgba(16, 185, 129, 0.05)',
          borderRadius: '8px',
          border: `1px solid ${useDemoMode ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)'}`,
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500
          }}>
            <input
              type="checkbox"
              checked={useDemoMode}
              onChange={(e) => setUseDemoMode(e.target.checked)}
              disabled={isConnecting}
              style={{
                cursor: isConnecting ? 'not-allowed' : 'pointer',
                width: '18px',
                height: '18px',
                accentColor: 'var(--accent)'
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: '4px' }}>
                {useDemoMode ? '🎭 Mode Démo' : '🔴 Mode Live (API Réelle)'}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', lineHeight: '1.5' }}>
                {useDemoMode
                  ? 'Utilisez des données fictives pour tester l\'interface (pas besoin de vraies clés)'
                  : 'Connectez-vous avec vos vraies clés API Crypto.com pour voir vos données réelles'}
              </div>
            </div>
            {!useDemoMode && (
              <span style={{
                padding: '4px 8px',
                background: '#10b981',
                color: 'white',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 600
              }}>
                LIVE
              </span>
            )}
          </label>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
            API Key
            <TooltipTerm content="Votre clé API Crypto.com Exchange. Créez-la dans Settings → API Management">
              <Icons.Info size={14} style={{ color: 'var(--muted)' }} />
            </TooltipTerm>
          </label>
          <input
            type="text"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Votre clé API Crypto.com"
            disabled={isConnecting}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              color: 'var(--text)',
              fontSize: '14px'
            }}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
            API Secret
            <TooltipTerm content="Votre clé secrète API. Conservez-la en sécurité et ne la partagez jamais">
              <Icons.Info size={14} style={{ color: 'var(--muted)' }} />
            </TooltipTerm>
          </label>
          <input
            type="password"
            value={apiSecret}
            onChange={(e) => setApiSecret(e.target.value)}
            placeholder="Votre clé secrète API"
            disabled={isConnecting}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              color: 'var(--text)',
              fontSize: '14px'
            }}
          />
        </div>

        <button
          onClick={handleConnect}
          disabled={!apiKey || !apiSecret || isConnecting}
          style={{
            width: '100%',
            padding: '12px',
            background: isConnecting ? 'var(--muted)' : 'var(--accent)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: (apiKey && apiSecret && !isConnecting) ? 'pointer' : 'not-allowed',
            opacity: (apiKey && apiSecret && !isConnecting) ? 1 : 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          {isConnecting ? (
            <>
              <Icons.Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
              Connexion en cours...
            </>
          ) : (
            <>
              <Icons.Activity size={18} />
              Se connecter à Crypto.com
            </>
          )}
        </button>

        <div style={{ marginTop: '24px', padding: '12px', background: 'var(--bg)', borderRadius: '6px' }}>
          <p style={{ fontSize: '12px', color: 'var(--muted)', lineHeight: '1.6', marginBottom: '8px' }}>
            <strong>ℹ️ Deux modes disponibles:</strong>
          </p>
          <ul style={{ fontSize: '11px', color: 'var(--muted)', lineHeight: '1.6', marginBottom: '8px', marginLeft: '16px' }}>
            <li><strong>Mode Démo (🎭):</strong> Testez avec des données fictives (clés &gt;10 caractères suffisent)</li>
            <li><strong>Mode Live (🔴):</strong> Connectez vos vraies clés API pour voir vos données réelles</li>
          </ul>
          <p style={{ fontSize: '11px', color: 'var(--muted)', lineHeight: '1.6', marginBottom: '8px' }}>
            <strong>Note:</strong> Le mode Live utilise un backend proxy Vercel pour contourner les restrictions CORS de l'API Crypto.com.
          </p>
          <button
            onClick={handleOpenCryptoComDocs}
            style={{
              marginTop: '8px',
              background: 'transparent',
              border: 'none',
              color: 'var(--accent)',
              fontSize: '12px',
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: 0
            }}
          >
            📖 Documentation API Crypto.com →
          </button>
        </div>
      </div>
    );
  }

  // Show loading spinner when initially fetching data after connection
  const isInitialLoading = isConnected && (botsLoading || stakingLoading || cardLoading);

  if (isInitialLoading) {
    return <LoadingSpinner size="lg" message="Chargement de vos données Crypto.com..." />;
  }

  return (
    <>
      {/* Header with Connection Status */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icons.CryptoCom size={32} />
            <div>
              <h1 style={{ fontSize: '24px', marginBottom: '4px' }}>
                Crypto.com Dashboard
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: cryptoComAPI.isDemoMode() ? '#f59e0b' : '#10b981',
                  animation: 'pulse 2s infinite'
                }}></div>
                <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
                  {cryptoComAPI.isDemoMode() ? 'Mode Démo • Données fictives' : 'Connecté • API Live'}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={handleDisconnect}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text)',
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Icons.X size={14} />
            Déconnexion
          </button>
        </div>
      </div>

      {/* Demo Mode Banner */}
      {cryptoComAPI.isDemoMode() && (
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          background: 'rgba(245, 158, 11, 0.1)',
          borderRadius: '8px',
          borderLeft: '4px solid #f59e0b'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <Icons.Info size={20} style={{ color: '#f59e0b', marginTop: '2px', flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#f59e0b' }}>
                Mode Démo Actif
              </p>
              <p style={{ fontSize: '13px', lineHeight: '1.6', color: 'var(--muted)', marginBottom: '8px' }}>
                Vous visualisez des <strong>données fictives</strong> pour tester l'interface.
                L'API Crypto.com ne peut pas être appelée directement depuis le navigateur pour des raisons de sécurité (CORS).
              </p>
              <p style={{ fontSize: '12px', lineHeight: '1.6', color: 'var(--muted)' }}>
                <strong>Pour utiliser vos vraies données:</strong> Déployez un backend proxy (ex: Vercel serverless function)
                qui fera le pont entre le frontend et l'API Crypto.com.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="metrics" style={{ marginBottom: '24px' }}>
        <MetricsCard
          label="Total Trading Profit"
          value={`$${totalTradingProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          color="green"
          icon={Icons.TrendingUp}
          tooltip="Profit combiné de tous vos bots de trading actifs et terminés"
        />
        <MetricsCard
          label="Total Staking Rewards"
          value={`$${totalStakingRewards.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          color="purple"
          icon={Icons.DollarSign}
          tooltip="Récompenses totales générées par toutes vos positions de staking"
        />
        <MetricsCard
          label="Active Bots"
          value={activeBots}
          color="blue"
          icon={Icons.Bot}
          tooltip="Nombre de bots de trading actuellement actifs"
        />
        <MetricsCard
          label="Total Staked Value"
          value={`$${totalStakedValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          color="orange"
          icon={Icons.Lock}
          tooltip="Valeur totale bloquée dans vos positions de staking"
        />
      </div>

      {/* Export Data */}
      <ExportData bots={tradingBots} positions={stakingPositions} />

      {/* Smart Recommendations (IA) */}
      <div style={{ marginBottom: '24px' }}>
        <SmartRecommendations
          bots={tradingBots}
          positions={stakingPositions}
          currentCard={croCard}
          croPrice={0.10}
        />
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '24px', borderBottom: '2px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          {[
            { id: 'bots' as const, label: 'Trading Bots', icon: Icons.Bot },
            { id: 'staking' as const, label: 'Staking', icon: Icons.Lock },
            { id: 'card' as const, label: 'CRO Card', icon: Icons.CreditCard }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                border: 'none',
                borderBottom: selectedTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent',
                color: selectedTab === tab.id ? 'var(--accent)' : 'var(--muted)',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '-2px'
              }}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Trading Bots Tab */}
      {selectedTab === 'bots' && (
        <>
          {/* Bot Performance Chart */}
          {tradingBots.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <BotPerformanceChart bots={tradingBots} />
            </div>
          )}

          <div className="card">
            <h2 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icons.Bot size={24} />
              Trading Bots
              {tradingBots.length > 0 && (
                <span style={{
                  fontSize: '14px',
                  color: 'var(--muted)',
                  fontWeight: 'normal'
                }}>
                  ({filteredBots.length}/{tradingBots.length})
                </span>
              )}
            </h2>

            {tradingBots.length === 0 ? (
            <EmptyState
              icon={Icons.Bot}
              title="Aucun bot de trading actif"
              description="Vous n'avez pas encore de bots de trading configurés sur Crypto.com Exchange"
              action={{
                label: 'Ouvrir Crypto.com Exchange',
                onClick: () => window.open('https://crypto.com/exchange/trade', '_blank'),
                icon: Icons.ExternalLink
              }}
              tips={[
                'Les bots Grid sont parfaits pour les marchés latéraux',
                'DCA réduit le risque en lissant votre prix d\'entrée',
                'TWAP minimise l\'impact sur le prix pour les gros ordres'
              ]}
              helpLink={{
                label: 'En savoir plus sur les bots de trading',
                onClick: () => window.open('https://help.crypto.com/en/articles/5015397-crypto-com-exchange-trading-bots', '_blank')
              }}
            />
          ) : (
            <>
              {/* Bot Filters */}
              <BotFilters bots={tradingBots} onFilterChange={setFilteredBots} />

              {filteredBots.length === 0 ? (
                <div style={{
                  padding: '32px',
                  textAlign: 'center',
                  color: 'var(--muted)'
                }}>
                  <Icons.Filter size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
                  <p style={{ fontSize: '14px' }}>Aucun bot ne correspond à vos filtres</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '16px' }}>
                  {filteredBots.map(bot => (
                <div
                  key={bot.id}
                  style={{
                    padding: '16px',
                    background: 'var(--bg)',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${getBotTypeColor(bot.type)}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '16px', fontWeight: 600 }}>{bot.symbol}</span>
                        <TooltipTerm content={getBotTypeTooltip(bot.type)}>
                          <span style={{
                            padding: '2px 8px',
                            background: getBotTypeColor(bot.type),
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'help'
                          }}>
                            {bot.type}
                          </span>
                        </TooltipTerm>
                        <span style={{
                          padding: '2px 8px',
                          background: bot.status === 'ACTIVE' ? '#10b981' : '#6b7280',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600
                        }}>
                          {bot.status}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                        Démarré il y a {Math.floor((Date.now() - bot.createdAt) / (1000 * 60 * 60 * 24))} jours
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '18px', fontWeight: 600, color: bot.profit >= 0 ? '#10b981' : '#ef4444' }}>
                        ${bot.profit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </div>
                      <div style={{ fontSize: '13px', color: bot.profitPercentage >= 0 ? '#10b981' : '#ef4444' }}>
                        {bot.profitPercentage >= 0 ? '+' : ''}{bot.profitPercentage.toFixed(2)}%
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Trades</div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>{bot.trades}</div>
                    </div>
                    {bot.gridLevels && (
                      <div>
                        <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>
                          <TooltipTerm content="Nombre de niveaux de prix dans la grille">
                            Grid Levels
                          </TooltipTerm>
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: 600 }}>{bot.gridLevels}</div>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Avg Trade</div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>
                        ${(bot.profit / bot.trades).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                    <button style={{
                      padding: '6px 12px',
                      background: 'var(--accent)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <Icons.Eye size={14} />
                      Détails
                    </button>
                    <button style={{
                      padding: '6px 12px',
                      background: 'transparent',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <Icons.ExternalLink size={14} />
                      Ouvrir sur Crypto.com
                    </button>
                  </div>
                </div>
              ))}
                </div>
              )}
            </>
          )}
          </div>
        </>
      )}

      {/* Staking Tab */}
      {selectedTab === 'staking' && (
        <>
          {/* Staking Rewards Chart */}
          {stakingPositions.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <StakingRewardsChart positions={stakingPositions} />
            </div>
          )}

          {/* APY Comparison Chart */}
          {stakingPositions.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <APYComparisonChart positions={stakingPositions} />
            </div>
          )}

          <div className="card">
            <h2 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icons.Lock size={24} />
              Positions de Staking
            </h2>

            {stakingPositions.length === 0 ? (
            <EmptyState
              icon={Icons.Lock}
              title="Aucune position de staking"
              description="Vous n'avez pas encore de positions de staking actives"
              action={{
                label: 'Découvrir le staking CRO',
                onClick: () => window.open('https://crypto.com/eea/staking', '_blank'),
                icon: Icons.ExternalLink
              }}
              tips={[
                'Le staking CRO offre jusqu\'à 12% APY',
                'Déverrouillez des avantages carte en stakant',
                'Les rewards sont distribués quotidiennement'
              ]}
            />
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {stakingPositions.map(position => (
                <div
                  key={position.id}
                  style={{
                    padding: '16px',
                    background: 'var(--bg)',
                    borderRadius: '8px',
                    borderLeft: '4px solid #8b5cf6'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '16px', fontWeight: 600 }}>{position.instrument}</span>
                        <span style={{
                          padding: '2px 8px',
                          background: '#8b5cf6',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600
                        }}>
                          {getStakingTypeLabel(position.type)}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                        {position.amount.toLocaleString()} {position.instrument} staké
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '18px', fontWeight: 600, color: '#10b981' }}>
                        <TooltipTerm content="Annual Percentage Yield - Rendement annuel avec intérêts composés">
                          {position.apy.toFixed(2)}% APY
                        </TooltipTerm>
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                        <TooltipTerm content="Annual Percentage Rate - Taux d'intérêt annuel simple">
                          {position.apr.toFixed(2)}% APR
                        </TooltipTerm>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Total Rewards</div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#10b981' }}>
                        ${position.totalRewards.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Pending Rewards</div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>
                        ${position.pendingRewards.toFixed(2)}
                      </div>
                    </div>
                    {position.lockupPeriod && (
                      <div>
                        <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>
                          <TooltipTerm content="Durée pendant laquelle vos tokens sont bloqués">
                            Lockup Period
                          </TooltipTerm>
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: 600 }}>
                          {position.lockupPeriod} jours
                        </div>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Status</div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#10b981' }}>
                        {position.status}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </>
      )}

      {/* CRO Card Tab */}
      {selectedTab === 'card' && croCard && (
        <div className="card">
          <h2 style={{ fontSize: '20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icons.CreditCard size={24} />
            CRO Card Benefits
          </h2>

          {/* Warning if unlock soon */}
          {croCard.daysRemaining < 30 && (
            <div style={{
              padding: '12px 16px',
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: '8px',
              borderLeft: '3px solid #f59e0b',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Icons.AlertCircle size={18} style={{ color: '#f59e0b' }} />
              <span style={{ fontSize: '13px', color: '#f59e0b' }}>
                ⏰ Votre stake CRO sera disponible dans {croCard.daysRemaining} jours
              </span>
            </div>
          )}

          <div style={{
            padding: '24px',
            background: `linear-gradient(135deg, ${getCardTierInfo(croCard.tier).color} 0%, ${getCardTierInfo(croCard.tier).color}dd 100%)`,
            borderRadius: '12px',
            color: croCard.tier === 'ICY_WHITE' ? '#000' : '#fff',
            marginBottom: '24px'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                {getCardTierInfo(croCard.tier).name}
              </div>
              <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
                {croCard.cashbackRate}% Cashback
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>Staked CRO</div>
                <div style={{ fontSize: '18px', fontWeight: 600 }}>
                  {croCard.stakedAmount.toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>Staking APR</div>
                <div style={{ fontSize: '18px', fontWeight: 600 }}>
                  {croCard.stakingAPR}%
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>Days Remaining</div>
                <div style={{ fontSize: '18px', fontWeight: 600 }}>
                  {croCard.daysRemaining}
                </div>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div style={{ display: 'grid', gap: '12px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
              Avantages de la Carte
            </h3>
            {[
              { icon: '🎵', name: 'Spotify Premium', value: '$10.99/mo', active: croCard.tier !== 'MIDNIGHT_BLUE', tooltip: 'Remboursement mensuel de votre abonnement Spotify Premium' },
              { icon: '🎬', name: 'Netflix Standard', value: '$15.49/mo', active: ['JADE_GREEN', 'ROYAL_INDIGO', 'FROSTED_ROSE_GOLD', 'ICY_WHITE', 'OBSIDIAN'].includes(croCard.tier), tooltip: 'Remboursement mensuel de votre abonnement Netflix' },
              { icon: '📦', name: 'Amazon Prime', value: '$14.99/mo', active: ['FROSTED_ROSE_GOLD', 'ICY_WHITE', 'OBSIDIAN'].includes(croCard.tier), tooltip: 'Remboursement mensuel de votre abonnement Amazon Prime' },
              { icon: '✈️', name: 'Airport Lounge Access', value: 'Unlimited', active: ['ICY_WHITE', 'OBSIDIAN'].includes(croCard.tier), tooltip: 'Accès illimité aux salons d\'aéroport via LoungeKey' }
            ].map((benefit, idx) => (
              <TooltipTerm key={idx} content={benefit.tooltip}>
                <div
                  style={{
                    padding: '12px 16px',
                    background: 'var(--bg)',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    opacity: benefit.active ? 1 : 0.4,
                    cursor: 'help'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>{benefit.icon}</span>
                    <span style={{ fontSize: '14px', fontWeight: 500 }}>{benefit.name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '13px', color: 'var(--muted)' }}>{benefit.value}</span>
                    {benefit.active ? (
                      <Icons.CheckCircle size={18} style={{ color: '#10b981' }} />
                    ) : (
                      <Icons.X size={18} style={{ color: '#6b7280' }} />
                    )}
                  </div>
                </div>
              </TooltipTerm>
            ))}
          </div>

          <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(99, 102, 241, 0.05)', borderRadius: '8px', borderLeft: '3px solid var(--accent)' }}>
            <p style={{ fontSize: '13px', lineHeight: '1.6', marginBottom: '8px' }}>
              <strong>💡 Conseil Pro:</strong> Maximisez vos avantages en utilisant votre carte pour tous vos achats quotidiens.
              Le cashback de {croCard.cashbackRate}% s'accumule et est versé en CRO chaque mois !
            </p>
            <button
              onClick={() => window.open('https://help.crypto.com/en/articles/2742447-crypto-com-visa-card-rewards-benefits', '_blank')}
              style={{
                marginTop: '8px',
                background: 'transparent',
                border: 'none',
                color: 'var(--accent)',
                fontSize: '12px',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0
              }}
            >
              En savoir plus sur les avantages →
            </button>
          </div>
        </div>
      )}

      {/* Card Benefits Calculator */}
      {selectedTab === 'card' && (
        <div style={{ marginTop: '24px' }}>
          <CardBenefitsCalculator currentCard={croCard} />
        </div>
      )}

      {/* Tier Progression Visualization */}
      {selectedTab === 'card' && (
        <div style={{ marginTop: '24px' }}>
          <TierProgressionVisualization currentCard={croCard} croPrice={0.10} />
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </>
  );
}
